// REACT IMPORTS
import React, { useMemo } from "react";

// MUI COMPONENTS
import {
	CircularProgress,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
} from "@mui/material";

// MUI ICONS
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckIcon from "@mui/icons-material/Check";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import EditTimeOffTypeDialog from "./EditTimeOffTypeDialog";
import { SortableHeaderCell, useSortedRows } from "./sortableTable";

// CONSTANTS
import { fmtDateTime } from "./timeOffConstants";

const AMBER = "#ca8a04";

// REACT COMPONENT
// dialogType is {} for a new type or the type being edited; the console owns it
// so its New Type button can sit on the tab row
const TimeOffTypesTab = ({ data, onChanged, dialogType, setDialogType }) => {
	const types = useMemo(() => data?.types || [], [data]);
	const activityName = useMemo(
		() => new Map((data?.activities || []).map((a) => [a.id, a.name])),
		[data],
	);
	const projectName = useMemo(
		() => new Map((data?.projects || []).map((p) => [p.id, p.name])),
		[data],
	);
	const bambooIds = useMemo(
		() => new Set((data?.bambooTypes || []).map((t) => t.id)),
		[data],
	);
	const tableIds = useMemo(
		() => new Set(types.map((t) => t.bamboohr_type_id)),
		[types],
	);

	// Sort values per column (activity/project sort by the names shown)
	const sortGetters = useMemo(
		() => ({
			bambooId: (t) => t.bamboohr_type_id,
			name: (t) => t.name,
			activity: (t) =>
				t.bqe_activity_id
					? activityName.get(t.bqe_activity_id) || t.bqe_activity_id
					: null,
			project: (t) =>
				t.bqe_project_id
					? projectName.get(t.bqe_project_id) || t.bqe_project_id
					: null,
			activityId: (t) => t.bqe_activity_id,
			postHours: (t) => t.post_hours,
			sync: (t) => t.is_synced,
			requests: (t) => t.request_count,
			updated: (t) => t.modified || t.created,
		}),
		[activityName, projectName],
	);
	const { sorted, sortKey, sortDir, onSort } = useSortedRows(
		types,
		sortGetters,
		"name",
		"asc",
		["postHours", "sync", "requests", "updated"],
	);
	const sortProps = { activeKey: sortKey, dir: sortDir, onSort };

	// BambooHR types with no row here never sync
	const missingFromTable = (data?.bambooTypes || []).filter(
		(t) => !tableIds.has(t.id),
	);
	const notReady = types.filter(
		(t) => t.is_synced && (!t.bqe_activity_id || !t.bqe_project_id),
	);

	if (!data) {
		return (
			<div className="loadingDiv">
				<CircularProgress color="primary" />
			</div>
		);
	}

	return (
		<div className="timeOffConsoleBody">
			{(missingFromTable.length > 0 || notReady.length > 0) && (
				<div className="timeOffConsoleToolbar">
					{missingFromTable.length > 0 && (
						<div className="timeOffConsoleWarning">
							<LinkOffIcon
								fontSize="small"
								style={{ color: AMBER }}
							/>
							{missingFromTable.map((t) => t.name).join(", ")}{" "}
							{missingFromTable.length === 1 ? "is" : "are"} in
							BambooHR but not mapped here. Requests of{" "}
							{missingFromTable.length === 1
								? "this type"
								: "these types"}{" "}
							aren't synced.
						</div>
					)}
					{notReady.length > 0 && (
						<div className="timeOffConsoleWarning">
							<LinkOffIcon
								fontSize="small"
								style={{ color: AMBER }}
							/>
							{notReady.map((t) => t.name).join(", ")}{" "}
							{notReady.length === 1 ? "is" : "are"} set to sync
							but missing a BQE activity or project.
						</div>
					)}
				</div>
			)}

			<TableContainer className="timeOffConsoleTableContainer">
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							<SortableHeaderCell
								sortKey="bambooId"
								{...sortProps}
							>
								BambooHR ID
							</SortableHeaderCell>
							<SortableHeaderCell sortKey="name" {...sortProps}>
								Name
							</SortableHeaderCell>
							<SortableHeaderCell
								sortKey="activity"
								{...sortProps}
							>
								BQE Activity
							</SortableHeaderCell>
							<SortableHeaderCell
								sortKey="project"
								{...sortProps}
							>
								BQE Project
							</SortableHeaderCell>
							<SortableHeaderCell
								sortKey="activityId"
								{...sortProps}
							>
								BQE Activity ID
							</SortableHeaderCell>
							<SortableHeaderCell
								sortKey="postHours"
								align="center"
								{...sortProps}
							>
								Post Hours
							</SortableHeaderCell>
							<SortableHeaderCell
								sortKey="sync"
								align="center"
								{...sortProps}
							>
								Sync
							</SortableHeaderCell>
							<SortableHeaderCell
								sortKey="requests"
								align="right"
								{...sortProps}
							>
								Requests
							</SortableHeaderCell>
							<SortableHeaderCell
								sortKey="updated"
								{...sortProps}
							>
								Updated
							</SortableHeaderCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{types.length === 0 && (
							<TableRow>
								<TableCell colSpan={10}>
									<J2NoResultsFound />
								</TableCell>
							</TableRow>
						)}
						{sorted.map((t) => {
							const activity = t.bqe_activity_id
								? activityName.get(t.bqe_activity_id) ||
									t.bqe_activity_id
								: null;
							const project = t.bqe_project_id
								? projectName.get(t.bqe_project_id) ||
									t.bqe_project_id
								: null;
							return (
								<TableRow
									key={t.id}
									hover
									style={{
										background: t.is_synced
											? undefined
											: "#fafafa",
									}}
								>
									<TableCell className="timeOffConsoleMono">
										{t.bamboohr_type_id}
									</TableCell>
									<TableCell>
										<div className="timeOffConsoleEmployee">
											{t.name}
										</div>
										{bambooIds.size > 0 &&
											!bambooIds.has(
												t.bamboohr_type_id,
											) && (
												<div className="timeOffConsoleMuted">
													Not in BambooHR
												</div>
											)}
									</TableCell>
									<TableCell>
										<span
											className="timeOffConsoleStatusCell"
											style={{
												color: activity
													? undefined
													: AMBER,
												fontWeight: activity
													? 400
													: 600,
											}}
										>
											{activity ? (
												<LinkIcon fontSize="small" />
											) : (
												<LinkOffIcon fontSize="small" />
											)}
											{activity || "Not mapped"}
										</span>
									</TableCell>
									<TableCell>{project || "—"}</TableCell>
									<TableCell className="timeOffConsoleMono timeOffConsoleNoWrap">
										{t.bqe_activity_id || "—"}
									</TableCell>
									<TableCell align="center">
										{t.post_hours ? (
											<CheckIcon
												fontSize="small"
												color="primary"
											/>
										) : (
											"—"
										)}
									</TableCell>
									<TableCell align="center">
										{t.is_synced ? (
											<CheckIcon
												fontSize="small"
												color="primary"
											/>
										) : (
											"Off"
										)}
									</TableCell>
									<TableCell
										align="right"
										style={{ fontWeight: 600 }}
									>
										{t.request_count}
									</TableCell>
									<TableCell className="timeOffConsoleMono timeOffConsoleNoWrap">
										{fmtDateTime(t.modified || t.created)}
									</TableCell>
									<TableCell align="right">
										<Tooltip
											title="Edit"
											arrow
											placement="top"
										>
											<IconButton
												size="small"
												onClick={() => setDialogType(t)}
											>
												<EditIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>

			<EditTimeOffTypeDialog
				open={Boolean(dialogType)}
				type={dialogType}
				activities={data.activities || []}
				projects={data.projects || []}
				bambooTypes={(data.bambooTypes || []).filter(
					(t) => !tableIds.has(t.id),
				)}
				onClose={() => setDialogType(null)}
				onSaved={() => {
					setDialogType(null);
					onChanged();
				}}
			/>
		</div>
	);
};

export default TimeOffTypesTab;
